import { useState, useEffect } from "react"
import { CompanyViewModel, ShopViewModel, LocationViewModel, TerminalViewModel, PaymentTerminalViewModel } from "../../Types/ExportTypes";
import { CurrencyAutoComplete, LanguageAutoComplete, CountryAutoComplete, Dimension1AutoComplete, TemplateCustomerAutoComplete } from "../AutoComplete/AutoCompletes";
import ImageBox from "../Image/ImageBox";
import AsyncAutoComplete from "../AsyncAutoComplete";
import { Option } from "../../Types/CommonTypes";
import InfoButton from "../Tooltip/InfoTextField";
import {
    Box,
    Button,
    TextField,
    Typography,
    Grid,
    FormControl,
    Checkbox,
    FormControlLabel,
    Switch,
    InputAdornment,
    Stepper, Step, StepLabel
} from "@mui/material"
import { useLoading, useAuth, useSnackbar, useNavigation } from "../../Context/Contexts";
import { useTranslation } from "react-i18next";
interface Props {
    cancelWizard: () => void;
}

export default function InitialSetupWizard({ cancelWizard }: Props) {
    const { authService } = useAuth();
    const { showLoading, hideLoading } = useLoading();
    const { showSnackbar } = useSnackbar();
    const { navigate, goBack } = useNavigation();

    const [step, setStep] = useState(0);
    const [company, setCompany] = useState<CompanyViewModel>({});
    const [shop, setShop] = useState<ShopViewModel>({});
    const [location, setLocation] = useState<LocationViewModel>({});
    const [terminal, setTerminal] = useState<TerminalViewModel>({});
    const [pendingCompanyImage, setPendingCompanyImage] = useState<File | null>(null);
    const [pendingCompanyImageUrl, setPendingCompanyImageUrl] = useState<string | null>(null);
    const [pendingShopImage, setPendingShopImage] = useState<File | null>(null);
    const [pendingShopImageUrl, setPendingShopImageUrl] = useState<string | null>(null);
    const [showContactInfoCompany, setShowContactInfoCompany] = useState(false);
    const { t } = useTranslation();
    //const handleNext = () => setStep((s) => s + 1)
    //const handleBack = () => setStep((s) => s - 1)

    const handleFinish = async () => {
        showLoading();

        try {
            var companyResponse = await authService.post<CompanyViewModel>("/companyApi/InitialSetupCompany", company);
            await uploadImage(pendingCompanyImage, companyResponse.unit);
            var shopResponse = await authService.post<ShopViewModel>(`/settingsApi/CreateOrUpdateShop`, shop);
            await uploadShopImage(pendingShopImage, shopResponse.shopNo)
            showSnackbar(t("dialog.companySetupSuccess"), "success");
            cancelWizard();
            hideLoading();
            navigate({ path: "/success/setup" });
        }
        catch (error: any) {
            showSnackbar(`${t("dialog.failedToFetchData")}: ${error.response?.data ?? error.message}`, "error");
        } finally {
            hideLoading();
        }

    }

    const uploadImage = async (file: File | null, unit?: string | null) => {
        try {
            if (!file) return;
            if (!unit) return;
            const formData = new FormData();
            formData.append("file", file);
            formData.append("unit", unit);

            const response = await authService.post<{ url: string }>("/companyApi/UploadImageForCompany", formData);
            if (response?.url) {
                showSnackbar(`${t("dialog.imageUploaded")}`, "success");
            } else {
                throw new Error("Failed to upload image.");
            }
        } catch (error) {
            console.error("Error uploading image:", error);
            showSnackbar(`${t("dialog.imageUploadError")}`, "error");
        } finally {
        }
    };

    const uploadShopImage = async (file: File | null, shopNo?: string | null) => {
        try {
            if (!file) return;
            if (!shopNo) return;
            const formData = new FormData();
            formData.append("file", file);
            formData.append("shopNo", shopNo);

            const response = await authService.post<{ url: string }>("/settingsApi/UploadImageForShop", formData);
            if (response?.url) {
                showSnackbar(`${t("dialog.imageUploaded")}`, "success");
            } else {
                throw new Error("Failed to upload image.");
            }
        } catch (error) {
            console.error("Error uploading image:", error);
            showSnackbar(`${t("dialog.imageUploadError")}`, "error");
        }
    };

    const handleCompanyLogoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;


        setPendingCompanyImage(file);
        setPendingCompanyImageUrl(URL.createObjectURL(file));

        if (pendingShopImage === null) {
            setPendingShopImage(file);
            setPendingShopImageUrl(URL.createObjectURL(file));
        }

    };


    const handleShopLogoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;


        setPendingShopImage(file);
        setPendingShopImageUrl(URL.createObjectURL(file));

    };

    const handleCompanyChange = (key: keyof CompanyViewModel, value: any) => {
      
        const updatedItem = { ...company, [key]: value };
        setCompany(updatedItem);

        const shopKeyMap: Partial<Record<keyof CompanyViewModel, keyof ShopViewModel>> = {
            name: "shopName",
            address: "shopAdress",
            address2: "shopAddress2",
            phoneNo: "phoneNo",
            eMail: "eMail",
            city: "city",
            postCode: "postCode",
            defaultLocationCode: "locationCode",
            

        };

        const mappedKey = shopKeyMap[key];
        if (mappedKey) {
            setShop(prev => {
                return { ...prev, [mappedKey]: value };
            });
        }
    };



    const handleShopChanges = (key: keyof ShopViewModel, value: any) => {

        const updatedItem = { ...shop, [key]: value };
        setShop(updatedItem);
    };



    const handleLocationChanges = (key: keyof LocationViewModel, value: any) => {
    
        const updatedItem = { ...location, [key]: value };
        setLocation(updatedItem);
    };

    const handleTerminalChanges = (key: keyof LocationViewModel, value: any) => {

        const updatedItem = { ...terminal, [key]: value };
        setTerminal(updatedItem);
    };

    const validateCompany = () => {
        if (!company) {
            showSnackbar(t("dialog.companyDataMissing"), "error");
            return false;
        }

        if (!company.name || company.name.trim() === "") {
            showSnackbar(t("dialog.companyNameRequired"), "warning");
            return false;
        }

        if (!company.blankCurrencText || company.blankCurrencText.trim() === "") {
            showSnackbar(t("dialog.companyCurrencyRequired"), "warning");
            return false;
        }

        if (!company.defaultLanguageCode || company.defaultLanguageCode.trim() === "") {
            showSnackbar(t("dialog.companyLanguageRequired"), "warning");
            return false;
        }




        return true;
    };


    const validateShop = () => {
        if (!shop) {
            showSnackbar(t("dialog.shopDataMissing"), "error");
            return false;
        }

        if (!shop.shopName || shop.shopName.trim() === "") {
            showSnackbar(t("dialog.shopNameRequired"), "warning");
            return false;
        }




        return true;
    };

    const validateStepAndNavigate = (newStep: number) => {
        if (newStep < 0) {
            setStep((s) => s + newStep)
            return;
        }
        //trying to go forward
        if (step === 0 && !validateCompany()) return;
        if (step === 1 && !validateShop()) return;

        setStep((s) => s + 1)
    }

    useEffect(() => {
        if (step === 3) {

        }
    }, [step]);

    return (
        <Box sx={{ width: "100%" }}>
            <Stepper activeStep={step} sx={{ mb: 3, overflowX: "auto" }} alternativeLabel>
                <Step><StepLabel> {t("common.company")}</StepLabel></Step>
                <Step><StepLabel> {t("common.shop")}</StepLabel></Step>
                {/*   <Step><StepLabel>Terminal</StepLabel></Step>*/}
            </Stepper>


            {//item info step
                step === 0 && (
                    <>
                        <Grid container spacing={2}>
                            {/* Editable fields */}
                            <Grid item xs={12}>
                                <Typography variant="h3"
                                    sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                    {t("common.setupInitialCompany")}
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                    {/* Current or Temporary Image Preview */}
                                    <ImageBox
                                        imageUrl={pendingCompanyImageUrl ?? ""}
                                        altText={company.name ?? ""}
                                        fallbackText={t("common.noImage")}
                                    />

                                    {/* Image Upload Input */}
                                    <Button
                                        variant="contained"
                                        component="label"
                                        sx={{ textTransform: "none", maxWidth: "250px", mt: 2 }}
                                    >
                                        {t("common.uploadImage")}
                                        <input
                                            type="file"
                                            accept="image/*"
                                            hidden
                                            onChange={(e) => {
                                                handleCompanyLogoFileChange(e);

                                            }}
                                        />
                                    </Button>
                                </Box>
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    label={t("common.name")}
                                    value={company.name}
                                    onChange={(e) => handleCompanyChange("name", e.target.value)}
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    margin="normal"
                                    error={!company.name || company.name.trim() === ""}
                                    helperText={
                                        !company.name || company.name.trim() === ""
                                            ? t("help.nameRequired")
                                            : ""
                                    }
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <InfoButton infoText={t("help.companyName")} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <FormControl fullWidth margin="normal">
                                    <CurrencyAutoComplete
                                        key={company.blankCurrencText || null}
                                        valueId={company.blankCurrencText || null}
                                        selectFirst={true}
                                        onChange={(selected) => handleCompanyChange("blankCurrencText", selected?.value || "")}
                                        infoText={t("help.defaultCurrency")}
                                        error={!company.blankCurrencText || company.blankCurrencText.trim() === ""}
                                        helperText={
                                            !company.blankCurrencText || company.blankCurrencText.trim() === ""
                                                ? t("help.defaultCurrencyRequired")
                                                : ""
                                        }
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} lg={6 }>
                                <TextField
                                    label={t("common.orgNo")}
                                    value={company.orgNo || ""}
                                    onChange={(e) => handleCompanyChange("orgNo", e.target.value)}
                                    fullWidth
                                    margin="normal"
                                />
                            </Grid>

                            <Grid item xs={12} lg={6}>
                                <TextField
                                    label={t("common.vatNo")}
                                    value={company.vatNo || ""}
                                    onChange={(e) => handleCompanyChange("vatNo", e.target.value)}
                                    fullWidth
                                    margin="normal"
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    label={t("common.address")}
                                    value={company.address}
                                    onChange={(e) => handleCompanyChange("address", e.target.value)}
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    margin="normal"
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <InfoButton infoText={t("help.companyAddress")} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label={t("common.address") + " 2"}
                                    value={company.address2}
                                    onChange={(e) => handleCompanyChange("address2", e.target.value)}
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    margin="normal"
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <InfoButton infoText={t("help.addressExtraInfo")} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    label={t("common.postCode")}
                                    value={company.postCode || ""}
                                    onChange={(e) => handleCompanyChange("postCode", e.target.value)}
                                    fullWidth
                                    margin="normal"
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    label={t("common.city")}
                                    value={company.city || ""}
                                    onChange={(e) => handleCompanyChange("city", e.target.value)}
                                    fullWidth
                                    margin="normal"
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <FormControl fullWidth margin="normal">
                                    <CountryAutoComplete
                                        key={company.countryCode || null}
                                        valueId={company.countryCode || null}
                                        selectFirst={true}
                                        onChange={(selected) => handleCompanyChange("countryCode", selected?.value || "")}
                                        infoText={t("help.defaultCountry")}
                                        error={!company.countryCode || company.countryCode.trim() === ""}
                                        helperText={
                                            !company.countryCode || company.countryCode.trim() === ""
                                                ? t("help.defaultCountryRequired")
                                                : ""
                                        }
                                    />
                                </FormControl>
                            </Grid>


                            <Grid item xs={12}>
                                <FormControl fullWidth margin="normal">
                                    <LanguageAutoComplete
                                        key={company.defaultLanguageCode || null}
                                        valueId={company.defaultLanguageCode || null}
                                        selectFirst={ true}
                                        onChange={(selected) => handleCompanyChange("defaultLanguageCode", selected?.value || "")}
                                        infoText={t("help.defaultLanguage")}
                                        error={!company.defaultLanguageCode || company.defaultLanguageCode.trim() === ""}
                                        helperText={
                                            !company.defaultLanguageCode || company.defaultLanguageCode.trim() === ""
                                                ? t("help.defaultLanguageRequired")
                                                : ""
                                        }
                                    />
                                </FormControl>
                            </Grid>

      


                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={<Switch checked={showContactInfoCompany} onChange={(e) => setShowContactInfoCompany(e.target.checked)} />}
                                label={t("common.showContactInfo")}
                            />
                        </Grid>

                        {
                            //<Grid item xs={12}>
                            //    <TextField
                            //        label={t("common.defaultEmail")}
                            //        value={company.eMail}
                            //        onChange={(e) => handleCompanyChange("eMail", e.target.value)}
                            //        fullWidth
                            //        InputLabelProps={{ shrink: true }}
                            //        margin="normal"
                            //        InputProps={{
                            //            endAdornment: (
                            //                <InputAdornment position="end">
                            //                    <InfoButton infoText={t("help.defaultEmail")} />
                            //                </InputAdornment>
                            //            ),
                            //        }}
                            //    />
                            //</Grid>

                        }
                        {showContactInfoCompany && (
                            <>
                                
                                <Grid item xs={12}>
                                    <TextField
                                        label={t("common.emailSenderAddress")}
                                        value={company.emailSenderAddress}
                                        onChange={(e) => handleCompanyChange("emailSenderAddress", e.target.value)}
                                        fullWidth
                                        InputLabelProps={{ shrink: true }}
                                        margin="normal"
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <InfoButton infoText={t("help.emailSenderAddress")} />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        label={t("common.custServiceEmail")}
                                        value={company.custServiceEmail}
                                        onChange={(e) => handleCompanyChange("custServiceEmail", e.target.value)}
                                        fullWidth
                                        InputLabelProps={{ shrink: true }}
                                        margin="normal"
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <InfoButton infoText={t("help.custServiceEmail")} />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        label={t("common.phoneNo")}
                                        value={company.custServicePhone}
                                        onChange={(e) => handleCompanyChange("custServicePhone", e.target.value)}
                                        fullWidth
                                        InputLabelProps={{ shrink: true }}
                                        margin="normal"
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <InfoButton infoText={t("help.custServicePhone")} />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>
                            </>
                        )}
                    </>
                )}
            {//store
                step === 1 && (<>
                    <Grid container spacing={2}>
                        <Grid xs={12}>
                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                {/* Current or Temporary Image Preview */}
                                <ImageBox
                                    imageUrl={pendingShopImageUrl ?? ""}
                                    altText={shop.shopName ?? ""}
                                    fallbackText={t("common.noImage")}
                                />

                                {/* Image Upload Input */}
                                <Button
                                    variant="contained"
                                    component="label"
                                    sx={{ textTransform: "none", maxWidth: "250px", mt: 2 }}
                                >
                                    {t("common.uploadImage")}
                                    <input
                                        type="file"
                                        accept="image/*"
                                        hidden
                                        onChange={(e) => {
                                            handleShopLogoFileChange(e);

                                        }}
                                    />
                                </Button>
                            </Box>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label={t("common.name")}
                                value={shop.shopName}
                                onChange={(e) => handleShopChanges("shopName", e.target.value)}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                margin="normal"
                                error={!shop.shopName || shop.shopName.trim() === ""}
                                helperText={
                                    !shop.shopName || shop.shopName.trim() === ""
                                        ? t("help.shopNameRequired")
                                        : ""
                                }
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <InfoButton infoText={t("help.shopNameInfo")} />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label={t("common.address")}
                                value={shop.shopAdress}
                                onChange={(e) => handleShopChanges("shopAdress", e.target.value)}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                margin="normal"
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <InfoButton infoText={t("help.shopAddress")} />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label={t("common.address2")}
                                value={shop.shopAddress2}
                                onChange={(e) => handleShopChanges("shopAddress2", e.target.value)}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                margin="normal"
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <InfoButton infoText={t("help.shopAddress2")} />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>

                        <Grid item xs={6}>
                            <TextField
                                label={t("common.postCode")}
                                value={company.postCode || ""}
                                onChange={(e) => handleShopChanges("postCode", e.target.value)}
                                fullWidth
                                margin="normal"
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                label={t("common.city")}
                                value={company.city || ""}
                                onChange={(e) => handleShopChanges("city", e.target.value)}
                                fullWidth
                                margin="normal"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth margin="normal">
                                <TemplateCustomerAutoComplete
                                    label={t("common.templateCustomerB2C")}
                                    key={shop.customerTemplateNo || null}
                                    valueId={shop.customerTemplateNo || null}
                                    selectFirst={ true}
                                    onChange={(selected) => handleShopChanges("customerTemplateNo", selected?.value || "")}
                                    infoText={t("help.templateCustomer")}
                                    error={!shop.customerTemplateNo || shop.customerTemplateNo.trim() === ""}
                                    helperText={
                                        !shop.customerTemplateNo || shop.customerTemplateNo.trim() === ""
                                            ? t("help.templateCustomerRequired")
                                            : ""
                                    }
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth margin="normal">
                                <Dimension1AutoComplete
                                    key={shop.globalDimension1Code || null}
                                    valueId={shop.globalDimension1Code || null}
                                    onChange={(selected) => handleShopChanges("globalDimension1Code", selected?.value || "")}
                                    infoText={t("help.dimension1")}
                                />
                            </FormControl>
                        </Grid>
                
                    </Grid>
                </>)}

            {//Terminal
                step === 2 && (<>
                    <Grid container spacing={2}>

                    </Grid>
                </>
                )}


            <Box sx={{ marginTop: 2, textAlign: "center" }}>
                {step < 1 ? (
                    <Button variant="contained" onClick={() => validateStepAndNavigate(1)}> {t("common.next")}</Button>
                ) : (
                    <Button variant="contained" color="primary" onClick={handleFinish}>{t("common.finish")}</Button>
                )}
                <Button disabled={step === 0} onClick={() => validateStepAndNavigate(-1)}> {t("common.back")}</Button>
                <Button variant="contained" color="warning" onClick={cancelWizard}>
                    {t("common.cancelWizard")}
                </Button>
            
            </Box>
        </Box>
    )
}
